"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import { KbdShortcut } from "../Kbd/KbdShortcut";
import styles from "./Tooltip.module.css";

export function TooltipProvider({
  delayDuration = 200,
  children,
}: {
  delayDuration?: number;
  children: ReactNode;
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export function Tooltip({
  children,
  open,
  defaultOpen,
  onOpenChange,
}: {
  children: ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Initial open state when uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </TooltipPrimitive.Root>
  );
}

export function TooltipTrigger({
  asChild,
  children,
}: {
  asChild?: boolean;
  children: ReactNode;
}) {
  return (
    <TooltipPrimitive.Trigger asChild={asChild}>
      {children}
    </TooltipPrimitive.Trigger>
  );
}

/**
 * Props for `TooltipContent`, the content bubble and the tooltip's primary
 * documented control surface.
 */
export interface TooltipProps {
  /** Extra class on the content bubble. */
  className?: string;
  /** Preferred placement relative to the trigger; flips when out of room. */
  side?: "top" | "right" | "bottom" | "left";
  /** Gap in px between the trigger and the bubble. */
  sideOffset?: number;
  /** Hint content — a short phrase, never interactive. */
  children: ReactNode;

  /**
   * Keys held together for the trigger's action, one entry per key; rendered
   * after the label as keycaps joined by plus signs. Modifier symbols (⌘ ⇧ ⌥ ⌃
   * ⌫ ↵) render as icons with a translated key name for assistive technology.
   */
  shortcut?: string[];
}

export function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  children,
  shortcut,
}: TooltipProps) {
  const hasShortcut = Boolean(shortcut?.length);
  const mergedClassName = [
    styles.content,
    hasShortcut && styles.withShortcut,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        side={side}
        sideOffset={sideOffset}
        className={mergedClassName}
      >
        {children}
        {hasShortcut && shortcut ? <KbdShortcut keys={shortcut} /> : null}
        <TooltipPrimitive.Arrow className={styles.arrow} width={10} height={5} />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}
